import { defineStore } from 'pinia'
import { type LocationQuery, type RouteParams, type RouteLocation } from 'vue-router'
import { handleLogin as handleLoginToken, handleLogout as handleLogoutToken, parseToken } from '@outputinc/o-logins'
import { UserProperties, setUserProperties, setUserId as setAmplitudeUserId } from '@outputinc/o-analytics'
import { brazeClient } from '@/utils/brazeClient'
import { useAuthStore } from '@/store/AuthStore'
import { useProductStore } from '@/store/ProductStore'
import { LoginResponse } from '@/types'
import router from '@/router'
import { computed, ref } from 'vue'
import gtmClient from '@/utils/gtmClient'
import { provideApolloClient, useMutation } from '@vue/apollo-composable'
import {
  postGoogleOAuthSignIn as postGoogleOAuthSignInMutation,
  postYamahaCognitoTokenExchange as postYamahaCognitoTokenExchangeMutation,
  login as loginMutation,
  createUser as createUserMutation
} from '@/queries'

import { apolloClient } from '@/utils/apolloClient'
import useRefreshAuth from '@/composables/refreshAuth'

const tokenCookieName = import.meta.env.VITE_OUTPUT_TOKEN_COOKIE_NAME
const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN
const refreshCookieName = import.meta.env.VITE_OUTPUT_REFRESH_COOKIE_NAME

export const useLoginStore = defineStore('LoginStore', () => {
  const isProcessing = ref(false)

  const login = ref({
    email: '',
    existingUserMessage: 'You already have an account, click Log In instead.',
    invalidEmailMessage: 'Please enter a valid email.',
    sameEmailMessage: 'New email must be different from current.',
    emptyPasswordMessage: 'Password cannot be empty.',
    invalidPasswordMessage: 'Please enter a valid password.',
    matchingPasswordMessage: 'Passwords must match.',
    differentPasswordMessage: 'New password must be different from current.',
    isInvalidEmail: false,
    password: '',
    query: {} as LocationQuery,
    params: {} as RouteParams
  })

  const appReferrer = ref<string | undefined>()

  const provideRoute = (query: LocationQuery, params: RouteParams) => {
    login.value = {
      ...login.value,
      query,
      params
    }
  }

  const emailIsValid = (email: string) => {
    const emailPrefix = email ? email.split('@')[0] : ''
    // Validates basic email syntax: _@_.__ & flags consecutive periods
    if (!/^[^\s#@]+@[^\s#@]+\.[^\s#@]{2,}$/.test(email) || /\.{2,}/.test(email)) {
      return false
    }
    // Flags invalid characters at beginning or end of prefix
    if (/^[\\._\-<>()[\]{}|\\\/'";:,?*&^%$#!+=]/.test(emailPrefix) || /[\\.\-<>()[\]{}|\\\/'";:,?*&^%$#!+=]$/.test(emailPrefix)) {
      return false
    }
    // Flags */&/=/' occurring anywhere in the email string
    if (/[\\*&='!%^()$]/.test(email)) {
      return false
    }
    return true
  }

  const passwordIsValid = (password: string): boolean => {
    return !!password && password.length >= 7 && /\d/.test(password) && /[a-z]/i.test(password)
  }

  const handleLoginResponse = (response: LoginResponse): void => {
    const { Events: GtmEvents, track: gtmTrackEvent } = gtmClient
    const { updateTokens, challengeId } = useAuthStore()

    const { apiKey, refreshToken } = response
    const { email, outputUid } = parseToken(apiKey)

    handleLoginToken(response, tokenCookieName, refreshCookieName, cookieDomain)
    updateTokens(apiKey, refreshToken)

    setAmplitudeUserId(outputUid)
    setUserProperties({ [UserProperties.EMAIL]: email })
    brazeClient.changeUser(outputUid)

    gtmTrackEvent(GtmEvents.WEBSITE_LOGIN_SUCCESS, { email, outputUid })

    // If there's a challengeId, post a refresh to complete the challenge
    if (challengeId) {
      const { postRefresh } = useRefreshAuth()
      postRefresh()
    }

    handleSuccess()
  }

  const handleSuccess = (): void => {
    const route = router.currentRoute
    const isLocalRoute = !!route.value.name

    const routeData = {
      ...route.value,
      name: 'success'
    }

    if (isLocalRoute) {
      router.push({
        ...routeData
      })
    }
    // send event
    sendRemoteUpdate(routeData)
  }

  const handleLogout = () => {
    const route = router.currentRoute

    const routeData = {
      ...route.value,
      name: 'signIn',
      path: 'signin'
    }

    handleLogoutToken(tokenCookieName, refreshCookieName, cookieDomain)

    const { clearTokens } = useAuthStore()
    clearTokens()

    isProcessing.value = false

    // send event
    sendRemoteUpdate(routeData)
  }

  const sendRemoteUpdate = (route: RouteLocation): void => {
    const { query, params } = login.value

    const detail = {
      ...route,
      ...(query && { query }),
      ...(params && { params })
    }
    const evt = new CustomEvent('remoteSuccess', { detail })
    document.dispatchEvent(evt)
  }

  const {
    mutate: postGoogleOAuthSignIn,
    onDone: onPostGoogleOAuthSignInDone,
    onError: onPostGoogleOAuthSignInError
  } = provideApolloClient(apolloClient)(() => useMutation(postGoogleOAuthSignInMutation))

  const {
    mutate: postYamahaCognitoTokenExchange,
    onDone: onPostYamahaCognitoTokenExchangeDone,
    onError: onPostYamahaCognitoTokenExchangeError
  } = provideApolloClient(apolloClient)(() => useMutation(postYamahaCognitoTokenExchangeMutation))

  const { mutate: postLogin, onDone: onLogin, onError: onLoginError } = provideApolloClient(apolloClient)(() => useMutation(loginMutation))

  const { mutate: postCreateUser, onDone: onCreateUser, onError: onCreateUserError } = provideApolloClient(apolloClient)(() => useMutation(createUserMutation))

  const setIsProcessing = (value: boolean) => (isProcessing.value = value)

  const setAppReferrer = (referrer: string) => {
    appReferrer.value = referrer
  }

  const { getAppReturnLink } = useProductStore()
  const appReturnLink = computed(() => (appReferrer.value ? getAppReturnLink(appReferrer.value) : undefined))

  const invalidPasswordMessage = computed(() => login.value.invalidPasswordMessage)
  const existingUserMessage = computed(() => login.value.existingUserMessage)
  const invalidEmailMessage = computed(() => login.value.invalidEmailMessage)
  const sameEmailMessage = computed(() => login.value.sameEmailMessage)
  const emptyPasswordMessage = computed(() => login.value.emptyPasswordMessage)
  const matchingPasswordMessage = computed(() => login.value.matchingPasswordMessage)
  const differentPasswordMessage = computed(() => login.value.differentPasswordMessage)
  const isInvalidEmail = computed(() => login.value.isInvalidEmail)
  const params = computed(() => login.value.params)
  const query = computed(() => login.value.query)

  return {
    appReferrer,
    appReturnLink,
    differentPasswordMessage,
    emailIsValid,
    emptyPasswordMessage,
    existingUserMessage,
    handleLoginResponse,
    handleLogout,
    invalidEmailMessage,
    invalidPasswordMessage,
    isInvalidEmail,
    isProcessing,
    matchingPasswordMessage,
    onCreateUser,
    onCreateUserError,
    onLogin,
    onLoginError,
    onPostGoogleOAuthSignInDone,
    onPostGoogleOAuthSignInError,
    onPostYamahaCognitoTokenExchangeDone,
    onPostYamahaCognitoTokenExchangeError,
    params,
    passwordIsValid,
    postCreateUser,
    postGoogleOAuthSignIn,
    postLogin,
    postYamahaCognitoTokenExchange,
    provideRoute,
    query,
    sameEmailMessage,
    sendRemoteUpdate,
    setAppReferrer,
    setIsProcessing
  }
})
